// Deploy pipeline verification (2026-07-23): confirm production deploys succeed. No behavior change.
import { resolveRelayerUrl } from "../../relayers/resolve";
import { FeatureFlagKey } from "../../features/types";
import { ChainIdEnum } from "../../../enums/chain";
import { WalletProviderKind } from "../../../enums/walletProvider";
import { ChainConfig } from "../types";
import {
  MONAD_BRANDING,
  RPC_WSS_URL,
  getPerpetualDexConfig,
  getTokenAddress,
  getUsdcAddress,
} from "./shared";

// ============================================
// MONAD TESTNET (Chain ID: 10143)
// ============================================
export const monadTestnetConfig: ChainConfig = {
  chainId: ChainIdEnum.MONAD_TESTNET,
  name: "Monad Testnet",
  shortName: "Monad",
  isTestnet: true,
  rpcUrls: [
    process.env.REACT_APP_RPC_URL ||
      "https://testnet-rpc.monad.xyz",
    // Public Monad testnet RPC — fallback when the primary is unavailable, and the
    // endpoint the revert-reason probe relies on (returns custom-error returndata).
    "https://testnet-rpc.monad.xyz",
  ],
  rpcWsUrls: [
    RPC_WSS_URL || "wss://testnet-rpc.monad.xyz",
    "wss://testnet-rpc.monad.xyz",
  ],
  wsUrl: process.env.REACT_APP_WSS_URL,
  explorerUrl: "https://testnet.monadexplorer.com",
  l2ExplorerUrl: "https://testnet.monadexplorer.com",
  nativeCurrency: { name: "MON", symbol: "MON", decimals: 18 },
  tokenDecimals: {
    usdc: 6,    // MUSDC has 6 decimals on-chain (verified via decimals() on testnet-rpc.monad.xyz, addr 0x9539…194B)
    usdt: 6,
    weth: 18,
    wbtc: 8,    // mWBTC has 8 decimals on-chain
    sdai: 6,
  },
  contracts: {
    erc20: {
      usdc: getUsdcAddress(ChainIdEnum.MONAD_TESTNET, "0x9539561aCf5FA7fAF32633E0169ad2C794F4194B"),
      "usdc-native": "",
      usdt: "",
      weth: getTokenAddress(ChainIdEnum.MONAD_TESTNET, "ETH", "0x4951e58e56c59CFb4eF298E7B014fB4179F590D7"),
      wbtc: getTokenAddress(ChainIdEnum.MONAD_TESTNET, "WBTC", "0xd1afF78cA7884Fa5C7ffFC41C28e21D804f2E89c"),
      mon: getTokenAddress(ChainIdEnum.MONAD_TESTNET, "WNATIVE", "0x059494A5e50a8909b61F1CF4692e05991f05d9D9"),
      l2usdc: "",
      l2usdt: "",
      l2weth: "",
      l2wbtc: "",
      govToken: "",
    },
    bridge: {
      l1DepositHelper: "",
      l1StandardBridge: "",
      l2StandardBridge: "",
      l2WithdrawProxy: "",
      exchangePass: "",
      strategies: "",
      staking: "",
    },
    perpetualDex: getPerpetualDexConfig(ChainIdEnum.MONAD_TESTNET, {
      router: "0x5c8cD43BC75F1f8AEd73bD056bC5b9101F19dcfE",
      crossMargin: "",
      orderHandler: "",
      orderVault: "",
      marketFactory: "0x2350B3F858Db857c91F6f973B194c5E066D358d4",
      usdc: "0x9539561aCf5FA7fAF32633E0169ad2C794F4194B",
      unifiedVault: "0xADF3DDE067268a964f380aca51dBa432d0895c0B",
      unifiedMarginAdapter: "",
      positionHandler: "",
      backstopVault: "0x6C8426325F2D5347231c839ac467dc703146bd63",
      oracle: "0x4Dac00b9cA769081480fBc6bfbfEDDfDbC0097eC",
      dataStore: "0xAa49f11dAf9C23c482e4039Ac41Bafd5CFFA0F39",
      marketHandler: "",
      balanceManager: "0x5c8cD43BC75F1f8AEd73bD056bC5b9101F19dcfE",
      perpRouter: "0x75EE96879ff153719173F5f1901D75911AC50ce8",
      agentRegistry: "0x61850C01201C58F805dA5b35f55921429d967740",
      markets: {
        "BTC-PERP": "0x3e261817222aAEF387e84cBa0c1bAfA4E4448C3B",
        "ETH-PERP": "0x4fa844A2292250fD33e6e6fd2349b11929A0EFE6",
        "MON-PERP": "0x1452c931e5C1DD243182925dA3D0D6Fea5EF0aDC",
        "TSLA-PERP": "0xBcAb130b255cbD988c48a11D902d3e77A20C3ecC",
        "NVDA-PERP": "0x69b16D9d89F3FE0464C33e5d8b5843f632e1D39D",
        "AAPL-PERP": "0x932b1b91cA01e57D7656F181BDe8c44A9d0a2a34",
        "XAU-PERP": "0x3CEd73D2e44750EEb248A6fCF3a78F927777Ab80",
        "XAG-PERP": "0x75eA6121A6492530f592f4b30D3F9F03987F259E",
        "EUR-PERP": "0x34D89b2AC6C4883024410a6a5a1E4db062908d9A",
        "JPY-PERP": "0x348a4B3Cc44607C0ee1792Bf84f51D9B0d86BA3E",
        "SPX-PERP": "0xf3a820328A2ff7bA27cA2F8B78Af6a219fFe33f0",
        "ANTHROPIC-PERP": "0x6903B68B180bF63f2E5A976cb46e3452a9618974",
      },
    }),
  },
  signingDomain: { name: "MarginX Monad Testnet", version: "1" },
  // WETH is now a standalone ERC20 (mock WETH), distinct from the native gas token
  // (MON / WNATIVE). The ETH collateral must read its ERC20 balance, not the native
  // balance — MON is exposed separately via the `mon` collateral.
  wethIsErc20Collateral: true,
  gasless: true,
  relayerUrl: resolveRelayerUrl(ChainIdEnum.MONAD_TESTNET),
  supportsSubaccounts: true,
  supportsTpsl: true,
  walletProvider: WalletProviderKind.PRIVY,
  // Spot CLOB is deployed here, so the spot vertical is available (it is off by
  // default and opted into per chain). See src/config/features.
  tradingTypes: {
    [FeatureFlagKey.PERPETUAL_SPOT]: true,
  },

  branding: MONAD_BRANDING,
};
